import React, { useCallback, useEffect, useState } from 'react'

import { Paper, Typography } from '@material-ui/core'
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridFilterModel,
  GridOverlay,
  GridSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'

import api from 'common/api'
import SendChatModal from 'routes/visits/Visits/View/CalendarEvent/SendChatModal'
import { useAppSelector } from 'store'
import { selectUserId } from 'store/user'

import { useTableHeightListener } from '../components/TableComponents/useTableHeightListener'
import CustomToolbar from './CustomToolbar'
import useColumns from './columns'
import { ApiRecord } from './types'

interface SelectedContact {
  contact: { name: string; id: string }
  highSchool: string
}

export const ManageVisitsTable = () => {
  const apiRef = useGridApiRef()
  const [tableHeight, ref] = useTableHeightListener()
  const userId = useAppSelector(selectUserId)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<ApiRecord[]>([])
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [],
    quickFilterValues: [''], // Initial search value
  })
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])
  const [selectedRows, setSelectedRows] = useState<ApiRecord[]>([])

  const [selectedContact, setSelectedContact] = useState<SelectedContact | null>(null)
  const [refreshAfterBulkAction, setRefreshAfterBulkAction] = useState(false)

  const columns = useColumns(({ contact, highSchool }) => {
    setSelectedContact({ contact, highSchool })
  })

  useEffect(() => {
    api.get<ApiRecord[]>('/api/college/visits').then((response) => {
      setRows(response ?? [])
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!refreshAfterBulkAction) return
    setLoading(true)
    api.get<ApiRecord[]>('/api/college/visits').then((response) => {
      setRows(response ?? [])
      setSelectedRows((response ?? []).filter((row) => selectionModel.includes(`${row.id}-${row.rep.repId}`)))
      setRefreshAfterBulkAction(false)
      setLoading(false)
    })
    // We only want to trigger this effect when refreshAfterBulkAction changes, not when selectionModel changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAfterBulkAction])

  const filterByHighSchoolSearch = useCallback((value: string) => {
    setFilterModel((prev) => ({
      ...prev,
      quickFilterValues: [value],
    }))
  }, [])

  const [firstName, ...rest] = (selectedContact?.contact.name ?? '').split(' ')
  const lastName = rest.join(' ')

  return (
    <Paper elevation={0}>
      <DataGridPro
        ref={ref}
        apiRef={apiRef}
        sx={{
          '&.MuiDataGrid-root': {
            height: `calc(100vh - ${tableHeight}px)`,
          },
        }}
        loading={loading}
        columns={columns}
        rows={rows}
        getRowId={(row) => `${row.id}-${row.rep.repId}`}
        filterModel={filterModel}
        onFilterModelChange={(model) => setFilterModel(model)}
        initialState={{
          pagination: {
            pageSize: 25,
          },
          sorting: {
            sortModel: [{ field: 'date', sort: 'asc' }],
          },
        }}
        sortingOrder={['desc', 'asc']}
        pagination
        rowsPerPageOptions={[25, 50, 100]}
        checkboxSelection
        onSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection)
          setSelectedRows(rows.filter((row) => newSelection.includes(`${row.id}-${row.rep.repId}`)))
        }}
        selectionModel={selectionModel}
        pinnedColumns={{ left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'highSchool'] }}
        components={{
          Toolbar: CustomToolbar,
          NoRowsOverlay: CustomEmptyState,
        }}
        componentsProps={{
          toolbar: {
            filterByHighSchoolSearch,
            apiRef,
            selectedRows,
            setRefreshAfterBulkAction,
          } as React.ComponentProps<typeof CustomToolbar>,
        }}
        disableColumnMenu
      />
      {selectedContact && (
        <SendChatModal
          userId={userId}
          show={true}
          onClose={() => setSelectedContact(null)}
          recipient={{
            userId: selectedContact.contact.id,
            firstName,
            lastName,
            school: selectedContact.highSchool,
          }}
        />
      )}
    </Paper>
  )
}

function CustomEmptyState() {
  return (
    <GridOverlay>
      <Typography>No upcoming visits scheduled</Typography>
    </GridOverlay>
  )
}

export default ManageVisitsTable
